import { useCallback, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';
import { usePostAbTestsV1ParticipantsAssignVariantTestSlugMutation, } from 'ab-tests-api';
const CONTROL_PARTICIPANT = { variant: { name: 'control' } };
const TREATMENT_PARTICIPANT = {
    variant: { name: 'treatment' },
};
/**
 * This hook is used to assign an AB variant to a test and persist the data in cookies for 7 days.
 * @param testSlug - The slug of the test to assign.
 * @param options.skip - Skip assignment and report treatment (see PersonalInfoV2 for why embeds skip).
 * @returns An object with the assignABVariant function, the data, the loading state, and the error state.
 */
function useABTest(testSlug, { skip = false } = {}) {
    const [data, setData] = useState(skip ? TREATMENT_PARTICIPANT : null);
    const [assignVariant, { isLoading, error }] = usePostAbTestsV1ParticipantsAssignVariantTestSlugMutation();
    const [cookies, setCookie] = useCookies([testSlug]);
    // useCookies returns a fresh `cookies` object on every change and universal-cookie
    // polls document.cookie every 300ms. Reading the cache through a ref keeps
    // assignABVariant referentially stable, so a cookie write the browser silently
    // rejects (cross-site iframe, cookies disabled) can't make the caller's effect
    // re-assign forever — universal-cookie emits a change on write, then another when
    // the poll finds the cookie missing, which previously looped indefinitely.
    const cookiesRef = useRef(cookies);
    cookiesRef.current = cookies;
    const hasAssignedRef = useRef(false);
    const assignVariantWrapper = useCallback(async () => {
        if (skip || hasAssignedRef.current)
            return;
        hasAssignedRef.current = true;
        const SEVEN_DAYS_FROM_NOW = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
        const cachedData = cookiesRef.current[testSlug];
        if (cachedData) {
            // Use the unexpired cached data.
            setData(cachedData);
            return;
        }
        try {
            // Call the API to assign a variant and persist the data in cookies for 7 days.
            const data = await assignVariant({
                testSlug,
                postAbTestsV1ParticipantsAssignVariantTestSlug: {},
            }).unwrap();
            setData(data);
            setCookie(testSlug, data, {
                expires: SEVEN_DAYS_FROM_NOW,
            });
        }
        catch {
            // Callers gate rendering on `data`, so falling back to control keeps a failed
            // assignment from leaving the view stuck on a spinner.
            setData(CONTROL_PARTICIPANT);
        }
    }, [assignVariant, testSlug, setCookie, skip]);
    return { assignABVariant: assignVariantWrapper, data, isLoading, error };
}
export default useABTest;
